import React, { useState, useEffect, useRef } from 'react';
import { usePOSStore } from '../../../stores/pos.store';
import { Button } from '../../common/Button';
import { X, Delete, ArrowRightLeft } from 'lucide-react';
import { useAuthStore } from '../../../stores';
import { useBaseCurrency, useActiveRate } from '../../../db/hooks/useLookupData';
import { formatCurrency, formatLocal } from '../../../lib/currency';
import { overlayStack } from '../../../lib/overlayStack';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  method: any;
  pending: number; // In base currency (USD/EUR)
}

export function POSPaymentModal({ isOpen, onClose, method, pending }: Props) {
  const { addPaymentEntry } = usePOSStore();
  const { profile, user } = useAuthStore();
  const { baseCurrency, currencySymbol } = useBaseCurrency();
  const { activeRate } = useActiveRate();
  const userName = profile?.name || user?.email?.split('@')[0] || 'Operador';
  
  const isVES = (method.currency === 'VES');
  const rateKey = baseCurrency.toLowerCase() as 'usd' | 'eur' | 'ves';
  const bcvRate = rateKey === 'eur' ? (activeRate?.eur || 1) : rateKey === 'usd' ? (activeRate?.usd || 1) : 1;

  const initialAmount = isVES && baseCurrency !== 'VES'
    ? (Math.round((pending * bcvRate + Number.EPSILON) * 100) / 100).toFixed(2)
    : pending.toFixed(2);

  const [amountStr, setAmountStr] = useState(initialAmount);
  const [reference, setReferencia] = useState('');

  useEffect(() => {
    if (isOpen) {
      const initAmt = isVES && baseCurrency !== 'VES'
        ? (Math.round((pending * bcvRate + Number.EPSILON) * 100) / 100).toFixed(2)
        : pending.toFixed(2);
      setAmountStr(initAmt);
      setReferencia('');
    }
  }, [isOpen, pending, isVES, baseCurrency, bcvRate]);

  // Register with overlay stack for ESC and mobile back button
  const overlayIdRef = useRef<string | null>(null);
  useEffect(() => {
    if (isOpen) {
      overlayIdRef.current = overlayStack.push(onClose);
    } else if (overlayIdRef.current) {
      overlayStack.remove(overlayIdRef.current);
      overlayIdRef.current = null;
    }
    return () => {
      if (overlayIdRef.current) {
        overlayStack.remove(overlayIdRef.current);
        overlayIdRef.current = null;
      }
    };
  }, [isOpen, onClose]);

  // Physical keyboard support for numpad
  useEffect(() => {
    if (!isOpen) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      // Ignore if user is typing in the reference input
      if ((e.target as HTMLElement)?.tagName === 'INPUT') return;
      if (e.key >= '0' && e.key <= '9') {
        e.preventDefault();
        handleNumpad(e.key);
      } else if (e.key === '.' || e.key === ',') {
        e.preventDefault();
        handleNumpad('.');
      } else if (e.key === 'Backspace') {
        e.preventDefault();
        handleNumpad('backspace');
      } else if (e.key === 'Enter') {
        e.preventDefault();
        handleAddPayment();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, amountStr]);

  if (!isOpen) return null;

  const isCash = method.name.toLowerCase().includes('efectivo');
  const numericInput = parseFloat(amountStr) || 0;
  
  // Calculate equivalent in base currency when input is in VES
  const equivalentBaseAmount = isVES && baseCurrency !== 'VES'
    ? numericInput / bcvRate
    : numericInput;

  const handleNumpad = (val: string) => {
    if (val === 'backspace') {
      setAmountStr(prev => prev.length > 1 ? prev.slice(0, -1) : '0');
    } else if (val === '.') {
      if (!amountStr.includes('.')) setAmountStr(prev => prev + '.');
    } else {
      setAmountStr(prev => prev === '0' ? val : prev + val);
    }
  };

  const handleAddPayment = () => {
    if (numericInput <= 0) return;
    
    // Always store the amount in base currency for pos store deduction
    addPaymentEntry({
      paymentMethodId: method.id,
      amount: Math.round((equivalentBaseAmount + Number.EPSILON) * 100) / 100,
      reference: reference.trim() || undefined
    });
    onClose();
  };

  const currentCurrencySymbol = isVES ? 'Bs.S' : currencySymbol;

  return (
    <div className="fixed inset-0 z-[90] flex items-center justify-center p-3 sm:p-4 bg-black/60 backdrop-blur-sm animate-fade-in">
      <div className="bg-surface border border-border rounded-3xl w-full max-w-sm max-h-[90vh] overflow-y-auto custom-scrollbar shadow-2xl">
        
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-border bg-surface-secondary/40">
          <div>
            <h3 className="font-extrabold text-text-primary text-base">
              Cobro en {method.name}
            </h3>
            <span className="text-xs font-semibold text-brand-sky">
              Moneda: {isVES ? 'Bolívares (VES)' : `${baseCurrency}`}
            </span>
          </div>
          <button onClick={onClose} className="p-1.5 text-text-tertiary hover:text-text-primary rounded-xl hover:bg-surface-tertiary transition-colors">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-6 pb-4">
          {/* Amount Display */}
          <div className="text-center mb-4 bg-surface-secondary p-4 rounded-2xl border border-border">
            <span className="text-xs font-bold text-text-secondary block mb-1">
              {isVES ? 'Monto a transferir / cobrar (Bs.S)' : `Monto en ${baseCurrency}`}
            </span>
            <span className="text-3xl font-black text-text-primary block">
              {currentCurrencySymbol} {amountStr || '0'}
            </span>

            {/* Equivalent live calculation */}
            {isVES && baseCurrency !== 'VES' && bcvRate > 1 && (
              <div className="mt-2 pt-2 border-t border-border/60 flex items-center justify-center gap-1.5 text-xs font-extrabold text-brand-sky">
                <ArrowRightLeft className="w-3.5 h-3.5" />
                <span>Acredita a la venta: {formatCurrency(equivalentBaseAmount, baseCurrency)}</span>
              </div>
            )}
          </div>

          {/* Reference for non-cash */}
          {!isCash && (
            <div className="mb-4">
              <input
                type="text"
                placeholder="Número de reference bancaria (Opcional)"
                value={reference}
                onChange={e => setReferencia(e.target.value)}
                className="w-full bg-surface border border-border rounded-xl px-4 py-2.5 text-xs text-text-primary text-center focus:border-brand-sky outline-none font-semibold"
              />
            </div>
          )}

          {/* Numpad */}
          <div className="grid grid-cols-3 gap-2.5 mb-4">
            {['1','2','3','4','5','6','7','8','9','.' ,'0'].map(num => (
              <button
                key={num}
                onClick={() => handleNumpad(num)}
                className="bg-surface-secondary hover:bg-surface-tertiary text-text-primary font-bold text-lg py-3 rounded-xl border border-border/50 transition-colors"
              >
                {num}
              </button>
            ))}
            <button
              data-testid="numpad-delete"
              onClick={() => handleNumpad('backspace')}
              className="bg-surface-secondary hover:bg-surface-tertiary text-text-primary font-bold text-lg py-3 rounded-xl border border-border/50 flex justify-center items-center transition-colors"
            >
              <Delete className="w-5 h-5 text-text-secondary" />
            </button>
          </div>

          {/* Footer details */}
          <div className="flex flex-col gap-1 mb-4 text-[11px] font-semibold text-text-secondary bg-surface-secondary/40 p-3 rounded-xl border border-border/40">
            <div className="flex justify-between">
              <span>Cajero registrador:</span>
              <span className="text-text-primary font-bold">{userName}</span>
            </div>
            <div className="flex justify-between">
              <span>Pendiente venta:</span>
              <span className="text-brand-orange font-extrabold">{formatCurrency(pending, baseCurrency)} {isVES && `(${formatLocal(pending * bcvRate)})`}</span>
            </div>
            {isVES && (
              <div className="flex justify-between text-brand-sky border-t border-border/40 pt-1 mt-1">
                <span>Tasa BCV del día:</span>
                <span className="font-extrabold">{formatLocal(bcvRate)} / {baseCurrency}</span>
              </div>
            )}
          </div>

          <Button variant="primary" fullWidth size="lg" onClick={handleAddPayment} className="bg-brand-orange hover:bg-brand-orange/90 text-white font-extrabold border-none shadow-lg shadow-brand-orange/20">
            Añadir Pago ({isVES ? `Bs.S ${amountStr}` : `${currencySymbol}${amountStr}`})
          </Button>
        </div>
      </div>
    </div>
  );
}
